import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Navbar from '../components/navbar';
import createApiInstance from '../utilities/apiAuth';
import Swal from 'sweetalert2';
import { useClient } from '../context/ClientContext';
import { useSelectedFranchisee } from '../context/SelectedFranchiseeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faUndo } from '@fortawesome/free-solid-svg-icons';

const Clients = () => {
  const { clients, selectedClient, handleClientSelect, refetchClients } = useClient();
  const { selectedFranchisee } = useSelectedFranchisee();
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const api = createApiInstance();

  const [formData, setFormData] = useState({
    client_name: '',
    rc_client_id: '',
    rc_client_secret: '',
    team_sheet_id: '',
    folder_id: '',
    hubspot_api_key: '',
    isEnterprise: true
  });

  useEffect(() => {
    console.log('Current clients:', clients);
    setLoading(false);
  }, [clients]);

  // Add initial data fetch
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        await refetchClients();
      } catch (error) {
        console.error('Error fetching initial clients:', error);
        toast.error('Failed to load clients');
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (
      selectedFranchisee &&
      selectedFranchisee.assoc_client_type &&
      Array.isArray(clients)
    ) {
      const matchedClient = clients.find(
        (client) => client.id === selectedFranchisee.assoc_client_type
      );
      if (matchedClient) {
        handleClientSelect(matchedClient);
      }
    }
  }, [selectedFranchisee, clients, handleClientSelect]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const result = await Swal.fire({
      title: editingClient ? 'Update Client?' : 'Create New Client?',
      text: editingClient
        ? 'Are you sure you want to update this client\'s information?'
        : 'Are you sure you want to create a new client?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      if (editingClient) {
        await api.put(`/clients/${editingClient.id}`, formData);
        toast.success('Client updated successfully');
      } else {
        await api.post('/clients', formData);
        toast.success('Client created successfully');
      }
      setShowModal(false);
      setEditingClient(null);
      setFormData({
        client_name: '',
        rc_client_id: '',
        rc_client_secret: '',
        team_sheet_id: '',
        folder_id: '',
        hubspot_api_key: '',
        isEnterprise: true
      });
      await refetchClients();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
      console.error('Error saving client:', error);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      client_name: client.client_name,
      rc_client_id: client.rc_client_id || '',
      rc_client_secret: client.rc_client_secret || '',
      team_sheet_id: client.team_sheet_id || '',
      folder_id: client.folder_id || '',
      hubspot_api_key: client.hubspot_api_key || '',
      isEnterprise: client.isEnterprise ?? false
    });
    setShowModal(true);
  };

  const handleDelete = async (client) => {
    const result = await Swal.fire({
      title: client.deletedAt ? 'Restore Client?' : 'Delete Client?',
      text: client.deletedAt
        ? 'Are you sure you want to restore this client?'
        : 'Are you sure you want to delete this client?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await api.patch(`/clients/${client.id}`, { restore: !!client.deletedAt });
      toast.success(client.deletedAt ? 'Client restored successfully' : 'Client deleted successfully');
      await refetchClients();
    } catch (error) {
      toast.error('Failed to delete client');
      console.error('Error deleting client:', error);
    }
  };

  const filteredClients = Array.isArray(clients)
    ? clients.filter((client) => {
        const matchesSearch = client.client_name.toLowerCase().includes(searchTerm.toLowerCase());
        if (showDeleted) {
          return matchesSearch;
        }
        return !client.deletedAt && matchesSearch;
      })
    : [];

  console.log('Filtered clients:', filteredClients);

  return (
    <div className='w-full flex flex-col min-h-screen bg-gray-900  space-y-8'>
      <Navbar />
      <div className='mx-auto flex flex-col p-4 justify-center items-center' style={{ width: '90rem' }}>
        <h1 className="mb-5 text-2xl text-hs-background text-center flex-grow">
          CLIENTS MANAGEMENT
        </h1>
        <div className="flex justify-between items-center w-full mb-4">
          <input
            type="text"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border rounded p-2 w-1/5"
          />

          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="showDeleted"
                checked={showDeleted}
                onChange={() => setShowDeleted((prev) => !prev)}
                className="form-checkbox h-5 w-5 text-gray-600"
              />
              <label htmlFor="showDeleted" className="text-white">
                Show Deleted
              </label>
            </div>
            <button
              onClick={() => {
                setEditingClient(null);
                setFormData({
                  client_name: '',
                  rc_client_id: '',
                  rc_client_secret: '',
                  team_sheet_id: '',
                  folder_id: '',
                  hubspot_api_key: '',
                  isEnterprise: true
                });
                setShowModal(true);
              }}
              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
            >
              Create Client
            </button>
          </div>
        </div>

        <div className="relative overflow-x-auto shadow-lg sm:rounded-lg" style={{ width: '88rem' }}>
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
              <p className="text-white mt-2">Loading clients...</p>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-white">No clients found</p>
            </div>
          ) : (
            <table className="w-full text-sm text-left rtl:text-right text-white cursor-pointer">
              <thead className="text-xs text-hs-background uppercase bg-hs-light-gray">
                <tr>
                  <th scope="col" className="px-4 py-3">Client Name</th>
                  <th scope="col" className="px-4 py-3">RC Client ID</th>
                  <th scope="col" className="px-4 py-3">Team Sheet ID</th>
                  <th scope="col" className="px-4 py-3">Folder ID</th>
                  <th scope="col" className="px-4 py-3">Enterprise</th>
                  <th scope="col" className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client) => (
                  <tr
                    key={client.id}
                    onClick={() => !client.deletedAt && handleClientSelect(client)}
                    className={`${
                      selectedClient?.id === client.id
                        ? 'bg-hs-orange text-white hover:bg-hs-orange'
                        : client.deletedAt
                        ? 'text-gray-100 hover:text-gray-500 hover:bg-hs-gray'
                        : 'hover:bg-hs-gray'
                    } bg-hs-background text-hs-dark-gray`}
                  >
                    <td className="px-4 py-4">{client.client_name}</td>
                    <td className="px-4 py-4">{client.rc_client_id || 'N/A'}</td>
                    <td className="px-4 py-4">{client.team_sheet_id || 'N/A'}</td>
                    <td className="px-4 py-4">{client.folder_id || 'N/A'}</td>
                    <td className="px-4 py-4">{client.isEnterprise ? 'Yes' : 'No'}</td>
                    <td className="px-4 py-4 text-sm flex flex-row">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(client);
                        }}
                        className="bg-hs-blue text-white px-3 py-2 rounded hover:bg-hs-dark-blue mr-2"
                        disabled={!!client.deletedAt}
                        title="Edit Client"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(client);
                        }}
                        className={`${
                          client.deletedAt
                            ? 'bg-green-500 hover:bg-green-600'
                            : 'bg-red-500 hover:bg-red-600'
                        } text-white px-3 py-2 rounded`}
                        title={client.deletedAt ? 'Restore Client' : 'Delete Client'}
                      >
                        <FontAwesomeIcon icon={client.deletedAt ? faUndo : faTrash} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-1/2">
            <h2 className="text-2xl font-bold mb-4">{editingClient ? 'Edit Client' : 'Add New Client'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Client Name</label>
                <input
                  type="text"
                  name="client_name"
                  value={formData.client_name}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">RC Client ID</label>
                <input
                  type="text"
                  name="rc_client_id"
                  value={formData.rc_client_id}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">RC Client Secret</label>
                <input
                  type="password"
                  name="rc_client_secret"
                  value={formData.rc_client_secret}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Team Sheet ID</label>
                <input
                  type="text"
                  name="team_sheet_id"
                  value={formData.team_sheet_id}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Folder ID</label>
                <input
                  type="text"
                  name="folder_id"
                  value={formData.folder_id}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">HubSpot API Key</label>
                <input
                  type="password"
                  name="hubspot_api_key"
                  value={formData.hubspot_api_key}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="isEnterprise"
                    checked={formData.isEnterprise}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      isEnterprise: e.target.checked
                    }))}
                    className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Enterprise Client</span>
                </label>
              </div>
              <div className="flex justify-end space-x-2 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingClient(null);
                  }}
                  className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                >
                  {editingClient ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients; 